import React, { useRef, useState, memo, useEffect, useMemo } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { useDrop, DropTargetMonitor, useDragLayer } from 'react-dnd';
import { throttle } from 'utils';
import CircularProgress from '@material-ui/core/CircularProgress';
import JobCard, {
  JobCardStyleType,
  JobCardViewMode
} from 'components/Schedule/JobCard/JobCard';
import {
  ScheduleAssignmentEvent,
  ScheduleAssignmentType,
  EngineerSchedule,
  JobDetailsFields,
  ScheduleAssignment,
  PinStatus
} from 'models/Schedule';
import {
  getTimeDiff,
  timeToPixels,
  getCardPosition,
  getPostcode,
  getClosestPosition,
  JobTimeBoundaries,
  getMinutesWaiting
} from 'components/Schedule/scheduleHelpers'; // prettier-ignore
import { scheduleStartTime } from 'constants/schedule';
import { AppointmentSlot, AppointmentStatus } from 'models/Appointment';
import { timelineStart } from 'views/Schedule/ScheduleView';
import Typography from 'components/Typography';
import { getJobCardStyleType } from 'utils/jobStatus';
import { getFixedJobCategory } from './fixedJobCategory';
import { DragHoverCallback } from '../EngineerList/EngineerList';
import { defaultSlotHighlight } from '../TimelineHeader';
import { EngineerInfo } from '../EngineerInfo/EngineerInfo';
import { getHydratedJobDetailsWithAssignedSchedule } from '../Map/scheduleMapHelpers';
import PublishButton from './PublishButton';
import { tunePosition } from '../scheduleHelpers';
import PublishDialog from '../PublishDialog/PublishDialog';
import { useEngineerUtilisation } from './hooks';

interface EngineerRowProps {
  engineerSchedule: EngineerSchedule;
  isLoading: boolean;
  selectedJobCardDetails: JobDetailsFields;
  selectedEngineer?: string;
  onChange: (change: ScheduleAssignmentEvent) => void;
  onDragHover: DragHoverCallback;
  onPublish?: (engineerID: string) => void;
  onJobCardSelect: (slot: AppointmentSlot) => void;
  handleJobCardOnClick: (value: JobDetailsFields) => void;
}
interface HighlightProps {
  scheduleAndEngineerShiftTimeDiff: number;
  shiftWidth: number;
  beforeShiftDuration: number;
  afterShiftDuration: number;
  afterShiftTime: number;
  mouseHover: boolean;
  engineerLacksJobType: boolean;
  isEngineerSelected?: boolean;
  hasAbortedAssignment: boolean;
}
export interface JobDndItem {
  type: string;
  appointmentId: string;
  engineerId: string;
  slot: AppointmentSlot;
  driveTimeTo: number;
  driveTimeFrom: number;
  label: string;
  jobType: string;
  duration: number;
  hideTravelTime: boolean;
  assigned: boolean;
  jobCardStyleType: JobCardStyleType;
}

const useStyles = makeStyles<Theme, HighlightProps>(
  theme =>
    createStyles({
      container: {
        width: '100%',
        height: 64, // Adjusted for two timelines + gap + padding
        display: 'flex',
        position: 'relative',
        padding: theme.spacing(1, 0),
        opacity: ({ engineerLacksJobType }) =>
          engineerLacksJobType ? 0.25 : 1,
        filter: ({ engineerLacksJobType }) =>
          engineerLacksJobType ? `grayscale(1)` : `grayscale(0)`,
        borderRadius: theme.spacing(0.5),
        backgroundColor: ({ mouseHover, isEngineerSelected }) => {
          if (isEngineerSelected) {
            return theme.palette.aegis.colour.grey.lighter;
          }
          if (mouseHover) {
            return theme.palette.schedule.timeline.unassignedRow.background;
          }

          return 'transparent';
        }
      },
      publishButton: {
        position: 'absolute',
        right: theme.spacing(1.5),
        top: '50%',
        transform: 'translateY(-50%)',
        cursor: 'pointer'
      },
      loading: {
        position: 'absolute',
        zIndex: 999,
        backgroundColor: '#eaeaea',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      },
      engineerInfoContainer: {
        display: 'flex',
        width: timelineStart - theme.spacing(1),
        marginLeft: theme.spacing(1),
        alignItems: 'center'
      },
      timelinesWrapper: {
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        width: 420 // Original width of a single timeline, now for the wrapper
      },
      timeline: {
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        width: '100%', // Timelines will take full width of timelinesWrapper
        height: 22, // Half of original 44px
        // The existing '& + $timeline' selector handles the margin for stacked timelines
        '& + $timeline': {
          marginTop: theme.spacing(0.5) // 4px gap
        }
      },
      singleTimeline: {
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        width: 420, // Match timelinesWrapper width
        height: 44 // Original full height for a single timeline
      },
      cardWrapper: {
        position: 'absolute',
        fontSize: 'initial',
        top: 0
      },
      spinner: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -10,
        marginLeft: -10
      },
      insertionPoint: {
        position: 'absolute',
        zIndex: 99,
        top: '2px', // Vertically center roughly
        left: 0, // This will be overridden by inline style: left: insertionPointX
        width: 2,
        height: 'calc(100% - 4px)', // Flexible height
        backgroundColor: theme.palette.schedule.timeline.row.insertionPointer
      },
      rowHighlight: {
        position: 'absolute',
        zIndex: 1,
        top: -1,
        left: ({ scheduleAndEngineerShiftTimeDiff }) =>
          scheduleAndEngineerShiftTimeDiff,
        width: ({ shiftWidth }) => shiftWidth,
        height: '100%', // Flexible height
        border: `1px dashed ${theme.palette.schedule.timeline.row.highlight}`
      },
      beforeShift: {
        position: 'absolute',
        zIndex: 2,
        left: ({ beforeShiftDuration }) => beforeShiftDuration,
        top: '50%',
        transform: 'translateY(-50%)',
        width: ({ scheduleAndEngineerShiftTimeDiff, beforeShiftDuration }) =>
          Math.max(0, scheduleAndEngineerShiftTimeDiff - beforeShiftDuration),
        height: ({ hasAbortedAssignment }) =>
          // 18 pixels is the height of the travel time
          // 44 pixels is the height of the job card
          hasAbortedAssignment ? theme.spacing(2) + 18 : theme.spacing(2) + 44,
        background: `repeating-linear-gradient(
        90deg, ${theme.palette.schedule.timeline.shiftOvertime.color}
        0px, ${theme.palette.schedule.timeline.shiftOvertime.color}
        2px,
        transparent 2px,
        transparent 5px
        )`
      },
      shiftTime: {
        position: 'absolute',
        zIndex: 0,
        left: ({ scheduleAndEngineerShiftTimeDiff }) =>
          scheduleAndEngineerShiftTimeDiff,
        top: '50%',
        transform: 'translateY(-50%)',
        width: ({ shiftWidth }) => shiftWidth,
        height: 18,
        background: () => {
          const transparent = alpha(theme.palette.common.white, 0);

          return `repeating-linear-gradient(
            90deg,
            ${transparent} 0px,
            ${transparent} 3px,
            ${theme.palette.jobStatus.assigned.background} 3px,
            ${theme.palette.jobStatus.assigned.background} 5px
            )`;
        }
      },
      shiftTimeTransparent: {
        background: 'none' // Use background shorthand for full override
      },
      afterShift: {
        position: 'absolute',
        zIndex: 2,
        left: ({ shiftWidth, scheduleAndEngineerShiftTimeDiff }) =>
          shiftWidth + scheduleAndEngineerShiftTimeDiff,
        top: '50%',
        transform: 'translateY(-50%)',
        width: ({ afterShiftTime }) => afterShiftTime,
        height: ({ hasAbortedAssignment }) =>
          hasAbortedAssignment ? theme.spacing(2) + 18 : theme.spacing(2) + 44,
        background: `repeating-linear-gradient(
        90deg,
        ${theme.palette.schedule.timeline.shiftOvertime.color} 0px,
        ${theme.palette.schedule.timeline.shiftOvertime.color} 2px,
        transparent 2px,
        transparent 5px
        )`
      }
    }),
  { name: 'EngineerRow' }
);

const calculateCardLeftPosition = (
  isPrimaryTimeline: boolean,
  assignment: ScheduleAssignment,
  engineerSchedule: EngineerSchedule,
  shiftXOffset: number, // This is the 'x' from the useStyles, representing the engineer's shift start offset
  scheduleStartTimeConstant: string, // This is the global 'scheduleStartTime'
  overrideCardLeftPx?: number
): number => {
  const cardWidthPx = timeToPixels(assignment.length);

  if (overrideCardLeftPx !== undefined) {
    return overrideCardLeftPx;
  }

  if (isPrimaryTimeline) {
    const relativeToShiftStartLeft = getCardPosition(
      engineerSchedule.engineer.startTime,
      assignment.arrivalTime,
      assignment.driveTimeTo
    );
    return relativeToShiftStartLeft + shiftXOffset;
  }

  // Secondary timeline (aborted assignments): center card within its original slot's time
  if (assignment.appointment && assignment.appointment.slot) {
    const { slot } = assignment.appointment;
    const slotStartTime = slot.startTime;
    const slotEndTime = slot.endTime;

    if (slotStartTime && slotEndTime) {
      const slotStartOffsetPx = timeToPixels(
        getTimeDiff(scheduleStartTimeConstant, slotStartTime)
      );
      const slotWidthPx = timeToPixels(getTimeDiff(slotStartTime, slotEndTime));
      const calculatedLeft =
        slotStartOffsetPx + (slotWidthPx - cardWidthPx) / 2;
      return Math.max(slotStartOffsetPx, calculatedLeft);
    }
  }

  // Fallback for secondary timeline if no slot info or invalid slot times: center in the middle of the timeline
  const timelineDivWidth = 420; // Fixed width of the timeline
  return (timelineDivWidth - cardWidthPx) / 2;
};

const EngineerRow = ({
  isLoading,
  engineerSchedule,
  selectedJobCardDetails,
  selectedEngineer,
  onChange,
  onDragHover,
  onJobCardSelect,
  onPublish,
  handleJobCardOnClick
}: EngineerRowProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const [insertionPosIndex, setInsertionPosIndex] = useState(-1);
  const [insertionPointX, setInsertionPointX] = useState(0);
  const [mouseHover, setMouseHover] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);

  const selectedEngineerId = selectedJobCardDetails.assigned?.engineerId;
  const isEngineerSelected =
    selectedJobCardDetails.isOpen &&
    selectedEngineerId === engineerSchedule.engineer.id;

  const { draggedJob } = useDragLayer((monitor: any) => ({
    draggedJob: monitor.getItem() as JobDndItem
  }));

  function sanityCheckIndex(index: number): number {
    if (index <= 0) {
      return 1;
    }
    // The highest index being passed should be that of the last (the END job)
    if (index >= engineerSchedule.assignments.length) {
      return engineerSchedule.assignments.length - 1;
    }
    return index;
  }

  // Calculate the position for row border highlight
  const scheduleAndEngineerShiftTimeDiff = timeToPixels(
    getTimeDiff(scheduleStartTime, engineerSchedule.engineer.startTime)
  );

  const shiftWidth = timeToPixels(
    getTimeDiff(
      engineerSchedule.engineer.startTime,
      engineerSchedule.engineer.endTime
    )
  );

  // Remove 'START' and 'END' job types
  const jobCount = engineerSchedule.assignments.filter(
    assignment =>
      assignment.type === ScheduleAssignmentType.Job ||
      assignment.type === ScheduleAssignmentType.Fixed
  ).length;

  const beforeShiftDuration = jobCount
    ? getCardPosition(
        scheduleStartTime,
        engineerSchedule.assignments[1].arrivalTime,
        engineerSchedule.assignments[1].driveTimeTo
      )
    : 0;

  // Calculate the time difference in minutes between the engineer's shift end time and the
  // departure time of the last job
  const afterShiftTime = useMemo(
    () =>
      jobCount
        ? Math.max(
            0,
            timeToPixels(
              getTimeDiff(
                engineerSchedule.engineer.endTime,
                engineerSchedule.assignments[
                  engineerSchedule.assignments.length - 2
                ].departureTime
              )
            )
          )
        : 0,
    [engineerSchedule.engineer, engineerSchedule.assignments]
  );

  const engineerLacksJobType = Boolean(
    draggedJob &&
      !engineerSchedule.engineer.jobTypes.includes(draggedJob.jobType)
  );

  const engineerScheduleAssignments = engineerSchedule.assignments.filter(
    assignment =>
      assignment.type !== ScheduleAssignmentType.Start &&
      assignment.type !== ScheduleAssignmentType.End
  );

  const engineerHasOvertime = afterShiftTime > 0;

  const getTravelTimeFrom = (isLastJob: boolean) =>
    (isLastJob &&
      engineerSchedule.assignments.find(
        assignment => assignment.type === ScheduleAssignmentType.End
      )?.driveTimeTo) ||
    0;
  const hasAbortedAssignment = useMemo(
    () =>
      engineerScheduleAssignments.some(
        assignment =>
          assignment.appointment.status === AppointmentStatus.ABORTED
      ),
    [engineerScheduleAssignments]
  );

  const abortedAssignments = useMemo(
    () =>
      engineerScheduleAssignments.filter(
        assignment =>
          assignment &&
          assignment.appointment.status === AppointmentStatus.ABORTED
      ),
    [engineerScheduleAssignments]
  );

  const classes = useStyles({
    shiftWidth,
    scheduleAndEngineerShiftTimeDiff,
    beforeShiftDuration,
    afterShiftDuration: timeToPixels(getTravelTimeFrom(true)),
    afterShiftTime: engineerHasOvertime
      ? afterShiftTime + timeToPixels(getTravelTimeFrom(true))
      : afterShiftTime,
    mouseHover,
    engineerLacksJobType,
    isEngineerSelected,
    hasAbortedAssignment
  });

  const positions: JobTimeBoundaries[] = [];

  const hover = (item: JobDndItem, monitor: DropTargetMonitor) => {
    if (monitor.canDrop()) {
      const containerX = ref?.current?.getBoundingClientRect().x || 0;
      const mouseX = monitor.getClientOffset()?.x || 0;
      const left = mouseX - containerX;
      const slotStartX = timeToPixels(
        getTimeDiff(scheduleStartTime, item.slot.startTime)
      );
      const slotEndX = timeToPixels(
        getTimeDiff(scheduleStartTime, item.slot.endTime)
      );

      // This is 1 because of START assignments
      let position: JobTimeBoundaries = { index: 1, x: slotStartX };

      if (positions.length) {
        const closestJobCard = getClosestPosition(positions, left);

        position = tunePosition(
          positions,
          slotStartX,
          slotEndX,
          closestJobCard
        );
      }

      setInsertionPosIndex(position.index);
      setInsertionPointX(position.x);

      return;
    }
    setInsertionPosIndex(-1);
    setInsertionPointX(-1);
  };

  const throttledHover = throttle(hover, 300);

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'Job',
    canDrop: (item: JobDndItem, monitor) => {
      if (engineerLacksJobType) {
        return false;
      }

      const found = engineerSchedule.assignments.some(
        assignment => assignment.appointment?.id === item.appointmentId
      );

      if (found) {
        return false;
      }

      const slotStart = timeToPixels(
        getTimeDiff(scheduleStartTime, item.slot.startTime)
      );
      const slotEnd = timeToPixels(
        getTimeDiff(scheduleStartTime, item.slot.endTime)
      );

      const containerX = ref?.current?.getBoundingClientRect().x || 0;
      const mouseX = monitor.getClientOffset()?.x || 0;
      const left = mouseX - containerX;

      return left >= slotStart && left <= slotEnd;
    },
    drop: (item: JobDndItem, monitor) => {
      const didDrop = monitor.didDrop();

      if (didDrop) {
        return;
      }

      onChange({
        action: 'add',
        data: {
          position: sanityCheckIndex(insertionPosIndex)
        },
        engineerId: engineerSchedule.engineer.id,
        id: item.appointmentId,
        type: ScheduleAssignmentType.Job
      });
    },
    hover: throttledHover,
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  });

  useEffect(() => {
    if (isOver) {
      onDragHover(engineerSchedule.engineer.id);
    } else {
      onDragHover(undefined);
    }
  }, [isOver]);

  const engineerFullName = `${engineerSchedule.engineer.firstName} ${engineerSchedule.engineer.lastName}`;

  const renderAssignment = (
    assignment: ScheduleAssignment,
    index: number,
    isPrimaryTimeline: boolean,
    halfHeight: boolean,
    travelTimeFrom: number,
    // Optional: if provided, this is the exact left pixel value to use for the card.
    // Used for the secondary timeline where layout is managed externally.
    overrideCardLeftPx?: number
  ): JSX.Element => {
    const cardLeftPx = calculateCardLeftPosition(
      isPrimaryTimeline,
      assignment,
      engineerSchedule,
      scheduleAndEngineerShiftTimeDiff, // engineer's shift offset from useStyles
      scheduleStartTime, // global constant
      overrideCardLeftPx
    );

    // For aborted jobs (on the secondary timeline), we want the JobCard's visual content
    // to start exactly at cardLeftPx. This means no preceding travel or waiting time visuals.
    // So, driveTimeTo and minutesWaiting should be 0 for the JobCard in this case.
    // For the primary timeline, use the actual values.
    const jobCardDriveTimeTo = !isPrimaryTimeline ? 0 : assignment.driveTimeTo;
    const jobCardDriveTimeFrom = !isPrimaryTimeline ? 0 : travelTimeFrom;
    const jobCardMinutesWaiting = !isPrimaryTimeline
      ? 0
      : getMinutesWaiting(assignment.arrivalTime, assignment.earliestStartTime);

    const postcode = getPostcode(assignment.location?.postcode);
    const jobCardLabel =
      assignment.type === ScheduleAssignmentType.Fixed && !postcode
        ? getFixedJobCategory(assignment.appointment?.reason)
        : postcode;

    // Only populate DND positions for the primary timeline
    if (isPrimaryTimeline) {
      if (assignment.type !== ScheduleAssignmentType.End) {
        if (assignment.type !== ScheduleAssignmentType.Start) {
          positions.push({
            index,
            x:
              cardLeftPx +
              timeToPixels(jobCardMinutesWaiting) + // Use the potentially modified waiting time for position calculation
              timeToPixels(assignment.length),
            type: assignment.type
          });
        } else {
          positions.push({
            index: 0,
            x: cardLeftPx,
            type: assignment.type
          });
        }
      }
    }

    return (
      <div
        key={
          assignment.appointment?.id ||
          engineerSchedule.engineer.id + assignment.type
        }
        className={classes.cardWrapper}
        style={{
          left: cardLeftPx
        }}
        onDragStart={() =>
          assignment.appointment && 'slot' in assignment.appointment
            ? onJobCardSelect(assignment.appointment.slot)
            : () => null
        }
        onDrag={() =>
          assignment.appointment && 'slot' in assignment.appointment
            ? onJobCardSelect(assignment.appointment.slot)
            : () => null
        }
        onDragEnd={() =>
          assignment.appointment && 'slot' in assignment.appointment
            ? onJobCardSelect(defaultSlotHighlight)
            : () => null
        }
        onClick={() => {
          if (assignment.type !== ScheduleAssignmentType.Fixed) {
            onDragHover(engineerSchedule.engineer.id);
          }

          if (assignment.appointment && 'slot' in assignment.appointment) {
            onJobCardSelect(assignment.appointment.slot);
          }

          if (assignment.type === ScheduleAssignmentType.Job) {
            handleJobCardOnClick(
              getHydratedJobDetailsWithAssignedSchedule(assignment, {
                engineerId: engineerSchedule.engineer.id,
                firstName: engineerSchedule.engineer.firstName,
                lastName: engineerSchedule.engineer.lastName,
                scheduleStatus: engineerSchedule.status
              })
            );
          }
        }}
        data-testid="jobCardWrapperClickHandler"
      >
        <JobCard
          halfHeight={halfHeight}
          index={index}
          jobCardStyleType={getJobCardStyleType(
            assignment.appointment,
            true,
            assignment.type === ScheduleAssignmentType.Fixed
          )}
          hasComplaints={Boolean(
            assignment.appointment
              ? assignment.appointment.complaints?.length
              : null
          )}
          isPinned={assignment.pinStatus === PinStatus.Pinned}
          canDrag={assignment.type != ScheduleAssignmentType.Fixed}
          appointmentId={assignment.appointment?.id}
          engineerId={engineerSchedule.engineer.id}
          duration={assignment.length}
          arrivalTime={assignment.arrivalTime}
          earliestStartTime={assignment.earliestStartTime}
          minutesWaiting={jobCardMinutesWaiting}
          driveTimeTo={jobCardDriveTimeTo}
          driveTimeFrom={jobCardDriveTimeFrom}
          slot={assignment.appointment?.slot}
          jobType={assignment.appointment?.jobType}
          type={assignment.appointment?.appointmentType?.type}
          label={jobCardLabel}
          viewMode={JobCardViewMode.TIMELINE}
          assigned
          onDrop={item =>
            onChange({
              action: 'remove',
              engineerId: engineerSchedule.engineer.id,
              id: item.appointmentId,
              type: ScheduleAssignmentType.Job
            })
          }
          selectedJobCardDetails={selectedJobCardDetails}
          selectedEngineer={selectedEngineer}
          isStartOrEndJob={[
            ScheduleAssignmentType.Start,
            ScheduleAssignmentType.End
          ].includes(assignment.type)}
          assigmentType={assignment.type}
        />
      </div>
    );
  };

  const engineerTravelTime = useMemo(
    () =>
      engineerSchedule.assignments.reduce(
        (acc, assignment) => acc + assignment.driveTimeTo,
        0
      ),
    [engineerSchedule.assignments]
  );

  drop(ref);

  const engineerUtilisation = useEngineerUtilisation(
    engineerSchedule,
    engineerTravelTime
  );

  const abortedCardSpacingPx = 6; // Define spacing between aborted cards in the same slot

  // Calculate positions for aborted job cards, handling groups in the same slot
  const abortedJobCardPositions = useMemo(() => {
    const calculatedAbortedPositions: (number | undefined)[] = new Array(
      abortedAssignments.length
    ).fill(undefined);
    const groupedBySlot = new Map<
      string,
      { assignment: ScheduleAssignment; originalIndex: number }[]
    >();

    // 1. Group aborted assignments by slot, remembering their original index
    abortedAssignments.forEach((assignment, index) => {
      if (
        assignment.appointment?.slot?.startTime &&
        assignment.appointment?.slot?.endTime
      ) {
        const slotKey = `${assignment.appointment.slot.startTime}-${assignment.appointment.slot.endTime}`;

        if (!groupedBySlot.has(slotKey)) {
          groupedBySlot.set(slotKey, []);
        }

        groupedBySlot.get(slotKey)?.push({ assignment, originalIndex: index });
      }
      // Assignments without a proper slot will have calculatedAbortedPositions[index] as undefined,
      // and calculateCardLeftPosition will use its fallback (center in timeline) if override is undefined.
    });

    // 2. Calculate layout for each group within its slot
    groupedBySlot.forEach(groupItems => {
      if (groupItems.length === 0) return;

      const firstAssignmentInGroup = groupItems[0].assignment;
      const { slot } = firstAssignmentInGroup.appointment; // Slot is guaranteed by grouping logic
      const slotStartTime = slot.startTime;
      const slotEndTime = slot.endTime;

      const slotStartOffsetPx = timeToPixels(
        getTimeDiff(scheduleStartTime, slotStartTime)
      );
      const slotWidthPx = timeToPixels(getTimeDiff(slotStartTime, slotEndTime));

      if (groupItems.length === 1) {
        // Single card in slot: center it (same logic as calculateCardLeftPosition would use)
        const { assignment, originalIndex } = groupItems[0];
        const cardWidthPx = timeToPixels(assignment.length);
        const calculatedLeft =
          slotStartOffsetPx + (slotWidthPx - cardWidthPx) / 2;
        calculatedAbortedPositions[originalIndex] = Math.max(
          slotStartOffsetPx,
          calculatedLeft
        );
      } else {
        // Multiple cards in the same slot: distribute them with spacing
        const totalJobCardsWidthInGroup = groupItems.reduce(
          (sum, item) => sum + timeToPixels(item.assignment.length),
          0
        );
        const totalSpacingInGroup =
          (groupItems.length - 1) * abortedCardSpacingPx;
        const groupContentWidth =
          totalJobCardsWidthInGroup + totalSpacingInGroup;

        // Center the entire group (cards + spacing) within the slot
        let currentX =
          slotStartOffsetPx +
          Math.max(0, (slotWidthPx - groupContentWidth) / 2);
        currentX = Math.max(slotStartOffsetPx, currentX); // Ensure group starts within slot boundaries

        groupItems.forEach((item, jobIndexInGroup) => {
          calculatedAbortedPositions[item.originalIndex] = currentX;
          currentX += timeToPixels(item.assignment.length);
          if (jobIndexInGroup < groupItems.length - 1) {
            currentX += abortedCardSpacingPx;
          }
        });
      }
    });
    return calculatedAbortedPositions;
  }, [abortedAssignments]); // Depends only on abortedAssignments list and constants

  return (
    <div
      className={classes.container}
      onMouseEnter={() => setMouseHover(true)}
      onMouseLeave={() => setMouseHover(false)}
      data-qa-id="engineerRow"
      data-testid="engineerRow"
      data-engineerid={engineerSchedule.engineer.id}
    >
      {isLoading && (
        <div
          className={classes.loading}
          data-testid="engineerRowLoading"
          data-qa-id="engineerRowLoading"
        >
          <CircularProgress
            size={20}
            className={classes.spinner}
            color="primary"
          />
        </div>
      )}
      <EngineerInfo
        firstName={engineerSchedule.engineer.firstName}
        lastName={engineerSchedule.engineer.lastName}
        jobCount={jobCount}
        travelTime={engineerTravelTime}
        unavailableReason={engineerSchedule.unavailableReason}
        scheduleStatus={engineerSchedule.status}
        engineerId={engineerSchedule.engineer.id}
        containerStyles={classes.engineerInfoContainer}
        engineerUtilisation={engineerUtilisation}
      />
      {hasAbortedAssignment ? (
        <div ref={ref} className={classes.timelinesWrapper}>
          {isOver && canDrop && (
            <div data-qa-id="rowHighlight" className={classes.rowHighlight} />
          )}
          {isOver && insertionPointX !== -1 && (
            <div
              style={{ left: insertionPointX }}
              className={classes.insertionPoint}
            />
          )}
          <div className={classes.timeline} data-testid="engineerTimeline">
            <>
              {jobCount === 0 ? null : (
                <div
                  data-testid="beforeShiftTime"
                  className={classes.beforeShift}
                />
              )}
              <div data-testid="shiftTime" className={classes.shiftTime} />
              <div
                data-testid="afterShiftTime"
                className={classes.afterShift}
              />
            </>
            {engineerScheduleAssignments
              .filter(
                assignment =>
                  assignment?.appointment?.status !== AppointmentStatus.ABORTED
              )
              .map((assignment, index, liveAssignments) => {
                return renderAssignment(
                  assignment,
                  index,
                  true,
                  true,
                  getTravelTimeFrom(index === liveAssignments.length - 1)
                );
              })}
          </div>
          <div
            className={classes.timeline}
            data-testid="engineerTimelineAborted"
          >
            {abortedAssignments.map((assignment, index) => {
              const overridePx = abortedJobCardPositions[index];
              return renderAssignment(
                assignment,
                index, // Index within abortedAssignments
                false, // isPrimaryTimeline
                true, // halfHeight
                0, // aborted jobs are excluded from travel
                overridePx // Pre-calculated left position
              );
            })}
          </div>
        </div>
      ) : (
        <div
          ref={ref}
          className={classes.singleTimeline}
          data-qa-id="engineerTimeline"
          data-testid="engineerTimeline"
        >
          {isOver && canDrop && (
            <div data-qa-id="rowHighlight" className={classes.rowHighlight} />
          )}
          {isOver && insertionPointX !== -1 && (
            <div
              style={{ left: insertionPointX }}
              className={classes.insertionPoint}
            />
          )}
          <>
            {jobCount === 0 ? null : (
              <div
                data-testid="beforeShiftTime"
                className={classes.beforeShift}
              />
            )}
            <div data-testid="shiftTime" className={classes.shiftTime} />
            {jobCount === 0 ? null : (
              <div
                data-testid="afterShiftTime"
                className={classes.afterShift}
              />
            )}
          </>
          {engineerScheduleAssignments.map((assignment, index) =>
            // When there's a single timeline (no aborted jobs),
            // assignments should be positioned like on a primary timeline (relative to shift)
            // and use full height for cards.
            renderAssignment(
              assignment,
              index,
              true,
              false,
              getTravelTimeFrom(
                index === engineerScheduleAssignments.length - 1
              )
            )
          )}
        </div>
      )}
      {!(mouseHover && onPublish) || (
        <div
          onClick={() => {
            setPublishDialogOpen(true);
            setMouseHover(false);
          }}
          className={classes.publishButton}
          data-testid="publishButton"
        >
          <PublishButton />
        </div>
      )}
      {publishDialogOpen && (
        <PublishDialog
          open={publishDialogOpen}
          onClose={() => setPublishDialogOpen(false)}
          onPublish={() => {
            if (onPublish) {
              onPublish(engineerSchedule.engineer.id);
              setPublishDialogOpen(false);
            }
          }}
        >
          <Typography variant="body" component="h3" display="block">
            {`Are you sure you would like to publish ${engineerFullName}'s schedule?`}
            <br />
            They will receive a notification and be able to call customers to
            confirm bookings.
          </Typography>
        </PublishDialog>
      )}
    </div>
  );
};

export default memo(EngineerRow);
